import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    CustomTable,
    CustomTableHeadersProps,
    CustomTableRowsProps,
    formatNumber,
    Paper,
    Pagination,
    EmptyState,
    MobileTable,
} from '@dev/ee-components';
import styled from 'styled-components';
import { isEmpty, map } from 'lodash-es';
import { LocationDetails, ReportLocationStats } from '@dev/ee-api/ee-api-models';

import useMobile from '@/services/hooks/useMobile';
import { getReportsState } from '@/store/reports/actions';
import { getAccountStore } from '@/store/account/actions';
import { getCampaignsStore } from '@/store/campaigns/actions';
import {
    TableWithCountryProps,
    WorldMapsEnum,
    GeographyToggleBtnValues,
    MobileTableProps,
} from '../CampaignReportGeographyInterface';
import {
    isClickTrackingCampaignEnabled,
    isOpenTrackingCampaignEnabled,
    isLinkClickTrackingEnabled,
} from '../../Overview/helperFunctions';

type CountryDataType = ReportLocationStats & LocationDetails;

type MobileWrapperProps = {
    isEmpty?: boolean;
};

const MobileTableWrapper = styled.div<MobileWrapperProps>`
    margin: ${(props: MobileWrapperProps): string => (props.isEmpty ? '32px auto 0' : '32px 0 0')};
`;

const TableRowCell = styled.div`
    text-align: right;
`;

const isWorldMapSelected = (selectedMap): boolean => selectedMap === WorldMapsEnum.World;

let columnWidth: Record<string, string> = { country: '80%', clicks: '10%', opens: '10%' };

const useCountryRows = (props): CustomTableRowsProps[] => {
    const { locationStats } = useSelector(getReportsState);
    const { locationCountryStats, pickedCampaign } = useSelector(getCampaignsStore);
    const {
        page,
        perPage,
        countryStatisticsFormat,
        selectedMap,
        countriesTableData,
        setCountriesTableData,
        searchResult,
    } = props;

    const isClicksTracked = isClickTrackingCampaignEnabled(pickedCampaign?.campaign);
    const isOpensTracked = isOpenTrackingCampaignEnabled(pickedCampaign?.campaign);

    const isNumberFormat = countryStatisticsFormat === GeographyToggleBtnValues.Number;
    const offset = (page - 1) * perPage;
    const limit = perPage * page;

    const getProperCountryList = (): ReportLocationStats[] | LocationDetails[] => {
        setCountriesTableData(
            isWorldMapSelected(selectedMap) ? locationStats?.dataSet : locationCountryStats?.dataSetByCountry
        );

        return countriesTableData;
    };

    const countriesList = isEmpty(searchResult) ? getProperCountryList()?.slice(offset, limit) : searchResult;

    return map(
        countriesList,
        ({ Country, ClickCount, ClickPercentage, OpenCount, OpenPercentage, State }: CountryDataType) => ({
            country: Country ?? State,
            ...(isOpensTracked
                ? {
                      opens: (
                          <TableRowCell>{isNumberFormat ? formatNumber(OpenCount) : `${OpenPercentage}%`}</TableRowCell>
                      ),
                  }
                : {}),
            ...(isClicksTracked
                ? {
                      clicks: (
                          <TableRowCell>
                              {isNumberFormat ? formatNumber(ClickCount) : `${ClickPercentage}%`}
                          </TableRowCell>
                      ),
                  }
                : {}),
            rowID: Country,
        })
    );
};

const MobileTableSection = (props: MobileTableProps): JSX.Element => {
    const { rows, headers, dataCount, text } = props;
    const mobileTableRows = map(rows, ({ rowID, ...rest }) => rest);

    return (
        <MobileTableWrapper isEmpty={!dataCount}>
            {!dataCount ? <EmptyState text={text} /> : <MobileTable headers={headers} rows={mobileTableRows} />}
        </MobileTableWrapper>
    );
};

const TableWithCountry: FC<TableWithCountryProps> = (props: TableWithCountryProps): JSX.Element => {
    const { locationStats } = useSelector(getReportsState);
    const { t } = useTranslation();
    const { locationCountryStats, pickedCampaign } = useSelector(getCampaignsStore);
    const { AdvancedOptions } = useSelector(getAccountStore);
    const { page, perPage, selectedMap, setPage, setPerPage } = props;
    const isMobile = useMobile();

    const isClicksTracked = isClickTrackingCampaignEnabled(pickedCampaign?.campaign);
    const isOpensTracked = isOpenTrackingCampaignEnabled(pickedCampaign?.campaign);
    const isLinkTracked = isLinkClickTrackingEnabled(AdvancedOptions);

    const headerName: CustomTableHeadersProps[] = [
        {
            id: 'country',
            label: t('country'),
        },
    ];
    const mobileHeaders = [t('country')];

    if (isOpensTracked) {
        headerName.push({
            id: 'opens',
            label: t('opens'),
            headerAlign: 'right',
        });
        mobileHeaders.push(t('opens'));
    }

    if (isClicksTracked) {
        headerName.push({
            id: 'clicks',
            label: t('clicks'),
            headerAlign: 'right',
        });
        mobileHeaders.push(t('clicks'));
    }

    if (isClicksTracked && isLinkTracked && !isOpensTracked) {
        columnWidth = { country: '90%', clicks: '10%' };
    }

    if (isOpensTracked && !isClicksTracked && !isLinkTracked) {
        columnWidth = { country: '90%', opens: '10%' };
    }

    const paginationData = isWorldMapSelected(selectedMap)
        ? locationStats?.totalCount
        : locationCountryStats?.totalCount;
    const statsLoading = locationStats?.loading || locationCountryStats?.loading;
    const rows = useCountryRows(props);

    const changePage = (page: number, perPage: number): void => {
        setPage(page);
        setPerPage(perPage);
    };

    return isMobile ? (
        <MobileTableSection dataCount={paginationData} rows={rows} headers={mobileHeaders} text={t('emptystate')} />
    ) : (
        <Paper padding={{ top: 24 }}>
            <CustomTable
                headers={headerName}
                rows={rows}
                isEmpty={paginationData === 0 && !statsLoading}
                loading={statsLoading}
                stickyHeader
                rowPadding={48}
                rowHeight={60}
                hideCheckboxes
                columnsWidth={columnWidth}
                emptyStateProps={{ text: t('emptystate') }}
                disableRowClick
                disableFirstRowBorderTop
            />
            <Pagination
                perPage={perPage}
                activePage={page}
                dataLength={paginationData || 0}
                handlePaginationChange={(page, perPage): void => changePage(page, perPage)}
            />
        </Paper>
    );
};

export default TableWithCountry;
